import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { XStack } from 'tamagui';
import { GBAlert, GBButton } from '@greenbackapp/ui';
import { BUSINESS_START_PATH } from '../../auth/paywallScope';
import { useBusinessOnSale } from '../../../hooks/usePublicPricing';
import { NAME_YOUR_BUSINESS, useGroupAwaitingFirstBusiness } from './PaidUnnamedBusinessBanner';

const BannerWrap = styled.div`
  margin: 16px 24px 0;
`;

interface PreviewBannerProps {
  /**
   * The preview shell (business_accounting off) is the only way into
   * /business for somebody with no business, so it offers Greenback Business
   * while it is on sale (PRD 86 slice 5; design D18). The live shell shows
   * this banner on routes that aren't live yet, to people who already have a
   * business, and leaves it out.
   */
  offersBusiness?: boolean;
}

/**
 * Shown at the top of every /business/* page. The business module is a
 * preview running entirely on sample data — no backend endpoints are
 * connected yet, and nothing reflects the user's real accounts.
 *
 * Someone who paid for Greenback Business but has not named it yet (D14)
 * lands here with the flag off; the banner gives them the way to name it.
 * That wins over selling them Business: they have already bought it.
 */
export const PreviewBanner: React.FC<PreviewBannerProps> = ({ offersBusiness = false }) => {
  const navigate = useNavigate();
  const awaiting = useGroupAwaitingFirstBusiness();

  return (
    <BannerWrap>
      <GBAlert
        variant="info"
        title="Greenback Business preview"
        message="You're exploring a preview built with sample data. Nothing here is connected to your real accounts yet."
      />
      {awaiting ? (
        <XStack marginTop="$2">
          <GBButton variant="primary" size="sm" onPress={() => navigate(BUSINESS_START_PATH)}>
            {NAME_YOUR_BUSINESS}
          </GBButton>
        </XStack>
      ) : (
        offersBusiness && <SetUpBusinessAction />
      )}
    </BannerWrap>
  );
};

/**
 * Drawn only once the price list confirms Greenback Business is on sale:
 * loading, failed and switched off all draw nothing (it fails closed). A
 * component of its own so a banner that offers nothing never asks.
 */
const SetUpBusinessAction: React.FC = () => {
  const navigate = useNavigate();
  if (!useBusinessOnSale()) return null;
  // A real button under the alert, as in EntitlementBanners: GBAlert's own
  // action has no role and no tab stop.
  return (
    <XStack marginTop="$2">
      <GBButton variant="outline" size="sm" onPress={() => navigate(BUSINESS_START_PATH)}>
        Set up Greenback Business
      </GBButton>
    </XStack>
  );
};

export default PreviewBanner;
